import { DragRotateAndZoom, defaults as OlDefaultInteractions, Pointer as PointerInteraction } from 'ol/interaction';
import { MMPERINCH } from '../../constants';

/**
 * Calculates milimeters to pixels for a given dpi
 *
 * @param {number} mm
 * @param {number} dpi
 * @returns {number}
 */
export function mmToPixels(mm, dpi) {
    return ((mm * dpi) / MMPERINCH);
}

/**
 * Calculates pixels to milimeters for a given dpi
 *
 * @param {number} pixels
 * @param {number} dpi
 * @returns {number}
 */
export function pixelsToMM(pixels, dpi) {
    return ((pixels * MMPERINCH) / dpi);
}

/**
 * Adjusts dragging map for scaling in preview
 */
// eslint-disable-next-line no-unused-vars
class CustomDrag extends PointerInteraction {
    constructor() {
        function handleDownEvent(evt) {
            this.displayScale = Number(document.getElementById(evt.map.values_.target).dataset.scale);
            const view = evt.map.getView();
            this.displayResolution = view.getResolution();
            this.displayCenter = view.getCenter();
            return true;
        }
        function handleDragEvent(evt) {
            const translateMousePixeldToMapCoords = (val, delta, scale, resolution) => (val + (((delta / scale) * window.devicePixelRatio) * resolution));
            if (evt.dragging) {
                const { movementX, movementY } = evt.originalEvent;
                this.displayCenter = [
                    translateMousePixeldToMapCoords(this.displayCenter[0], -movementX, this.displayScale, this.displayResolution),
                    translateMousePixeldToMapCoords(this.displayCenter[1], movementY, this.displayScale, this.displayResolution)
                ];

                const view = evt.map.getView();
                view.setCenter(this.displayCenter);
                evt.stopPropagation();
            }
        }

        super({
            handleDownEvent: handleDownEvent,
            handleDragEvent: handleDragEvent,
        });

        /**
        * @type {array|undefined}
        * @private
        */
        this.displayCenter = undefined;

        /**
        * @type {number|undefined}
        * @private
        */
        this.displayResolution = undefined;

        /**
        * @type {number|undefined}
        * @private
        */
        this.displayScale = undefined;
    }
}

/**
 * Creates map based on an existing map to a destination map
 * Sources will load high resolution versions when highResolution parameter is set to true
 * Progress can be reflected in the object set in loadMonitor parameter
 * @returns {ol/Map} OpenLayers map object
 */
export async function renderPrintMap(store, mapName, destinationMapId) {
    const interactions = OlDefaultInteractions(
        {
            altShiftDragRotate: true,
            pinchRotate: false,
            doubleClickZoom: false,
            keyboard: false,
            mouseWheelZoom: true,
            shiftDragZoom: false,
            pinchZoom: false,
            zoomDelta: false
        }
    ).extend([new DragRotateAndZoom()]);//.extend([new CustomDrag()]);

    await store.commit('REGISTER_MAP', {
        name: mapName,
        id: destinationMapId,
        interactions
    });

    const printMap = store.getters.getMapByName(mapName);
    return printMap.map;
}

export async function renderLayers(store, mapName, referenceMap, highResolution, loadMonitor) {
    store.dispatch('updateViewPortMap', {
        ...referenceMap.viewport,
        mapName
    });

    if (referenceMap.highlight) {
        await store.commit('MAP_ADD_HIGHLIGHT_BORDER', {
            mapName,
            coords: referenceMap.highlight
        });
    }

    const firstbaselayer = referenceMap.baseLayer;
    if (firstbaselayer) {
        await store.dispatch('addLayerToMap', {
            mapName,
            id: firstbaselayer.id,
            highQuality: highResolution
        });
    }

    const activatedLayers = store.getters.getActivatedLayers;
    const addLayerPromisses = activatedLayers.map((layer) =>
        store.dispatch('addLayerToMap', {
            mapName,
            id: layer.id,
            highQuality: highResolution
        })
    );
    await Promise.all(addLayerPromisses);

    const printMap = store.getters.getMapByName(mapName);

    // Copy layer opacity from mainmap
    const mainMap = store.getters.getMapByName('main');

    printMap.layers.forEach((layer) => {
        const mainLayer = mainMap.layers.find(_layer => _layer.id === layer.id);
        layer.olLayer.setOpacity(mainLayer.olLayer.getOpacity());
    });


    Object.keys(referenceMap.customLayers).forEach(customLayerName => {
        printMap.customLayers[customLayerName].getSource().addFeatures(referenceMap.customLayers[customLayerName].getSource().getFeatures());
    });

    let printMapLayersSources = null;

    // Add layer sources if there are any.
    if (printMap?.layers) {
        printMapLayersSources = [
            ...printMap.layers.map((layer) => layer.olLayer.getSource())
        ];
    }

    // Add baselayer source if there is one.
    if (printMap.baseLayer) {
        printMapLayersSources.push(printMap.baseLayer.olLayer.getSource());
    }

    printMapLayersSources.forEach((source) => {
        source.on('tileloadstart', () => {
            loadMonitor.start += 1;
        });

        source.on('tileloadend', () => {
            loadMonitor.loaded += 1;
        });
        source.on('tileloaderror', () => {
            loadMonitor.error += 1;
        });
    });

    const map = printMap.map;

    map.once('postrender', () => {
        map.on(
            'moveend',
            () => {
                store.dispatch('mapMoveEnd', { mapName });
            }
        );
        map.on(
            'pointerdrag',
            () => {
                store.dispatch('mapPointerdrag', { mapName });
            }
        );
        // On Zoom change
        map.getView().on(
            'change:resolution',
            () => {
                store.dispatch('mapResolutionChanged', { mapName });
            }
        );
    });
}
